import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import { changedRowClass, type RunPosition } from '@/features/ai-changeset/utils/changeset-view';
import { DraftTag } from '@/features/menu/components/DraftTag';
import { ProductDraftLines } from '@/features/menu/components/ProductDraftLines';
import type { Draft, ProductView } from '@/features/menu/utils/menu-draft';
import type { ProductIssues } from '@/features/menu/utils/menu-checklist';
import { useT } from '@/i18n';
import { mediaSrc } from '@/lib/media-url';
import { loc } from '@/utils/localized';
import { formatMoney } from '@/utils/money';
import { Chip, ListItem } from 'framework7-react';
import { type MouseEvent as ReactMouseEvent, memo } from 'react';

const MUTED = '#8e8e93';

function thumbSrc(stored: string): string {
  return /^https?:\/\//i.test(stored) ? stored : mediaSrc(stored, 'small');
}

function productThumb(stored: string | undefined) {
  if (!stored) return null;
  return (
    <div
      slot="media"
      style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <img
        src={thumbSrc(stored)}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
}

function prettyCode(c: string) {
  return c.toLowerCase().replace(/_/g, ' ');
}

function flagChipClass(draft: Draft<boolean> | undefined, onWhen: boolean): string | undefined {
  if (!draft) return undefined;
  return draft.after === onWhen ? 'ai-changed-chip' : 'quick-edit-old';
}

function ProductIssueLine({ id, issues }: { id: string; issues: ProductIssues }) {
  const c = useT().menuChecklist;
  const notes = [
    ...(issues.noPrice ? [c.noPrice] : []),
    ...issues.unknownCodes.map((code) => c.unknownCode(code)),
  ];
  return (
    <span className="menu-item-issue" data-testid={`menu-item-issue-${id}`}>
      <i className="f7-icons" aria-hidden="true">
        exclamationmark_triangle_fill
      </i>
      {notes.join(' · ')}
    </span>
  );
}

const ProductRowTitle = memo(function ProductRowTitle({
  view,
  currency,
  issues,
}: {
  view: ProductView;
  currency: string;
  issues: ProductIssues | undefined;
  slot: string;
}) {
  const t = useT();
  const p = view.product;
  const { name, inStock, active, codes, labels, status } = view;
  const isActive = active?.after ?? p.isActive;
  const hiddenShown = !isActive || !!active;
  const stockShown = !(inStock?.after ?? p.inStock) || !!inStock;
  const chips = labels ? [] : p.labels;
  const shownCodes = codes ? null : p.allergenCodes;
  return (
    <div className="rsv-stack">
      <span className="rsv-stack-title">
        {p.code && (
          <span style={{ color: MUTED, fontVariantNumeric: 'tabular-nums', marginRight: 6 }}>
            {p.code}
          </span>
        )}
        <span className={status === 'removed' ? 'quick-edit-old' : undefined}>
          {name ? <ProposedValue start before={name.before} after={name.after} /> : loc(p.name)}
        </span>
        {status && <DraftTag status={status} testId={`menu-item-tag-${p.id}`} />}
      </span>
      {(chips.length > 0 ||
        hiddenShown ||
        stockShown ||
        codes ||
        labels ||
        p.options.length > 0 ||
        p.allergenCodes.length > 0) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
            color: MUTED,
          }}
        >
          {hiddenShown && (
            <Chip
              text={t.menu.hidden}
              outline
              className={flagChipClass(active, false)}
              style={{ height: 20 }}
            />
          )}
          {stockShown && (
            <Chip
              text={t.menu.outOfStock}
              color="orange"
              className={flagChipClass(inStock, false)}
              style={{ height: 20 }}
            />
          )}
          {chips.slice(0, 4).map((x) => (
            <Chip key={x} text={prettyCode(x)} outline style={{ height: 20 }} />
          ))}
          {chips.length > 4 && <span>{`+${chips.length - 4}`}</span>}
          {labels && (
            <ProposedValue
              start
              before={labels.before.map(prettyCode).join(', ')}
              after={labels.after.map(prettyCode).join(', ')}
            />
          )}
          {shownCodes && shownCodes.length > 0 && (
            <span style={{ color: 'var(--f7-theme-color)', fontWeight: 500 }}>
              {shownCodes.join(', ')}
            </span>
          )}
          {codes && (
            <ProposedValue
              start
              before={codes.before.join(', ') || t.menuQuickEdit.none}
              after={codes.after.join(', ') || t.menuQuickEdit.none}
            />
          )}
          {p.options.length > 0 && (
            <span style={{ marginLeft: chips.length ? 4 : 0 }}>
              {t.menu.optionsCount(p.options.length)}
            </span>
          )}
        </div>
      )}
      <ProductDraftLines view={view} currency={currency} />
      {issues && <ProductIssueLine id={p.id} issues={issues} />}
    </div>
  );
});

const ProductRowPrice = memo(function ProductRowPrice({
  view,
  currency,
  onPriceClick,
}: {
  view: ProductView;
  currency: string;
  slot: string;
  onPriceClick?: (productId: string) => void;
}) {
  const t = useT();
  const { before, after } = view.price;
  const shown = after ?? before;
  if (!shown) return null;
  const content = (
    <>
      {shown.isFrom && (
        <span style={{ fontSize: 11, color: MUTED, fontWeight: 400, marginRight: 3 }}>
          {t.menu.priceFrom}
        </span>
      )}
      {before === null ? (
        <span className="ai-changed-text">{formatMoney(shown.amount, currency)}</span>
      ) : after && after.amount !== before.amount ? (
        <ProposedValue
          before={formatMoney(before.amount, currency)}
          after={formatMoney(after.amount, currency)}
        />
      ) : (
        formatMoney(before.amount, currency)
      )}
    </>
  );
  if (!onPriceClick) return <div className="menu-item-price">{content}</div>;
  return (
    <button
      type="button"
      className="menu-item-price menu-item-price--tappable prevent-active-state-propagation"
      onClick={(e: ReactMouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onPriceClick(view.product.id);
      }}
    >
      {content}
    </button>
  );
});

export function MenuProductRow({
  view,
  run,
  store,
  currency,
  issues,
  onOpen,
  onPriceClick,
}: {
  view: ProductView;
  run: RunPosition | null;
  store: NavigationStore;
  currency: string;
  issues?: ProductIssues;
  onOpen: (() => void) | null;
  onPriceClick?: (productId: string) => void;
}) {
  const p = view.product;
  const muted =
    !(view.active?.after ?? p.isActive) ||
    !(view.inStock?.after ?? p.inStock) ||
    view.status === 'removed';
  const classes = [
    'menu-item-row',
    muted && 'menu-item-row--muted',
    view.status === 'removed' && 'menu-item-row--removed',
    changedRowClass(run),
  ].filter(Boolean);
  return (
    <ListItem
      key={p.id}
      link={onOpen ? '#' : undefined}
      onClick={onOpen ?? undefined}
      className={classes.join(' ')}
      data-quick-edit-target={p.id}
      data-view-row={p.id}
      data-testid={`menu-item-${p.id}`}
    >
      {view.changed && (
        <ChangePulseMarker
          slot="root-start"
          store={store}
          id={p.id}
          testId={`menu-item-pulse-${p.id}`}
        />
      )}
      {productThumb(loc(p.imageUrl) || undefined)}
      <ProductRowTitle slot="title" view={view} currency={currency} issues={issues} />
      <ProductRowPrice slot="after" view={view} currency={currency} onPriceClick={onPriceClick} />
    </ListItem>
  );
}
